import { useMemo, useRef, useState } from 'react';
import { message } from 'antd';
import { useGetPromotions, useUploadPayouts } from '../../../../domain/Promotions';
import { parsePayoutCsv, validatePayoutRows } from '../payoutCsv';
import { parseErrorMessage } from '../utils';

export const STEP = { SELECT: 'select', REVIEW: 'review', SUMMARY: 'summary' };

// FileReader keeps the CSV parsing on the client — nothing leaves the browser
// until the BE submit step.
const readFileText = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(reader.error || new Error('read_error'));
    reader.readAsText(file);
  });

// Owns the payout upload flow: promotion selection, client-side CSV parsing +
// validation (review), and the BE submit (result summary). Keeps the modal and
// its step components presentational.
export const usePayoutUpload = ({ promotion }) => {
  const { promotions } = useGetPromotions();
  // Disabled promotions can't receive new payouts, so they're not selectable.
  const activePromotions = useMemo(
    () => (promotions || []).filter((item) => !item.isDisabled),
    [promotions],
  );

  const [selectedId, setSelectedId] = useState(promotion?.id ?? '');
  const [file, setFile] = useState(null);
  const [step, setStep] = useState(STEP.SELECT);
  const [review, setReview] = useState(null);
  const [result, setResult] = useState(null);
  const [isReading, setIsReading] = useState(false);
  const fileInputRef = useRef(null);

  const selectedPromotion = useMemo(
    () => activePromotions.find((item) => item.id === selectedId) || null,
    [activePromotions, selectedId],
  );

  const uploadMutation = useUploadPayouts({
    onSuccess: (data) => {
      setResult(data);
      setStep(STEP.SUMMARY);
    },
    onError: (error) =>
      message.error(
        parseErrorMessage(error, 'Could not submit the payouts. Please try again.'),
      ),
  });

  const handleFileChange = (event) => {
    const [picked] = event.target.files || [];
    setFile(picked || null);
  };

  const handleUpload = async () => {
    if (!selectedId) {
      message.error('Please select a promotion.');
      return;
    }
    if (!file) {
      message.error('Please choose a CSV file.');
      return;
    }

    setIsReading(true);
    try {
      const text = await readFileText(file);
      const parsed = parsePayoutCsv(text);

      if (parsed.length === 0) {
        message.error('This file has no payout rows.');
        return;
      }

      setReview(validatePayoutRows(parsed, selectedPromotion?.maxValueCents));
      setStep(STEP.REVIEW);
    } catch (error) {
      message.error('Could not read the file. Please try again.');
    } finally {
      setIsReading(false);
    }
  };

  // The whole CSV is sent: the BE re-validates and its "payout failed" report is
  // the union of validation-rejected and transfer-failed rows.
  const handleSubmit = () => {
    if (!review || review.valid.length === 0 || uploadMutation.isPending) return;
    uploadMutation.mutate({ promotionId: selectedId, file });
  };

  return {
    step,
    activePromotions,
    selectedId,
    setSelectedId,
    file,
    fileInputRef,
    handleFileChange,
    isReading,
    review,
    result,
    isSubmitting: uploadMutation.isPending,
    handleUpload,
    handleSubmit,
    goBack: () => setStep(STEP.SELECT),
  };
};
